/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import React, { useEffect, useState } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';

import { useRovoButtonEventsInstrumentation } from '../../../../hooks/useRovoButtonEventsInstrumentation';
import { AIMiniModal, preloadAIMiniModal } from '../../../ai-mini-modal';
import type { AISmartButtonRefreshProps } from '../../../common/types/shared-types';
import { selectionCountsInMainEditor } from '../../utils/pmSelectionCounts';
import { AISmartButtonUI } from '../ai-smart-button-ui';

const styles = cssMap({ wrapper: { position: 'relative' } });

/**
 * This is the new AIFC chat refresh button. Controlled by `rovo_chat_refresh_v1_experiment` flag.
 */
export const AISmartButtonRefresh = ({
	product,
	cloudId,
	isNav4Enabled,
	sidebarApi,
	productPage,
	orgId,
	getActions,
	analyticsAttributes,
	topOffset,
	isDisabled = false,
}: AISmartButtonRefreshProps) => {
	const hoverTimerRef = React.useRef<NodeJS.Timeout | null>(null);
	const { fireRovoButtonUIEvent } = useRovoButtonEventsInstrumentation();
	const { mode, toggleConversationAssistantUIMode, setIsMiniModalAvailable } =
		useRovoExperienceAPIStore();

	const [isComponentLoaded, setIsComponentLoaded] = useState(false);

	useEffect(() => {
		setIsMiniModalAvailable(true);
		return () => {
			setIsMiniModalAvailable(false);
		};
	}, [setIsMiniModalAvailable]);

	useEffect(() => {
		fireRovoButtonUIEvent({
			source: productPage,
			action: 'rendered',
			actionSubject: 'rovoButton',
			actionSubjectId: 'aiSmartButton',
			attributes: {
				product,
				productPage,
				...analyticsAttributes,
				version: 'refresh',
				legacy: false,
			},
			product,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		fireRovoButtonUIEvent,
		product,
		productPage,
		// Note: analyticsAttributes is intentionally excluded from dependencies to prevent
		// infinite re-renders when the object reference changes. The analytics event
		// should only fire once when the component mounts.
	]);

	const buttonRef = React.useRef<HTMLButtonElement | null>(null);

	const onAISmartButtonClick = React.useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			toggleConversationAssistantUIMode({
				targetMode: ConversationAssistantUIModes.MINI_MODAL,
				sidebarApi,
			});

			const selectionAttributes: { totalSelectedChars?: number; totalSelectedWords?: number } = {};
			if (fg('platform_editor_ai_aifc_analytics')) {
				const counts = selectionCountsInMainEditor(e.currentTarget);
				if (counts != null) {
					selectionAttributes.totalSelectedWords = counts.words;
					selectionAttributes.totalSelectedChars = counts.chars;
				}
			}

			fireRovoButtonUIEvent({
				source: productPage,
				action: 'clicked',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiRovoSmartButton',
				attributes: {
					product,
					productPage,
					...analyticsAttributes,
					...selectionAttributes,
					version: 'refresh',
				},
				product,
			});
		},
		[
			product,
			productPage,
			analyticsAttributes,
			fireRovoButtonUIEvent,
			toggleConversationAssistantUIMode,
			sidebarApi,
		],
	);

	const onAISmartButtonEnter = React.useCallback(() => {
		if (!isComponentLoaded) {
			preloadAIMiniModal();
			setIsComponentLoaded(true);
		}

		hoverTimerRef.current = setTimeout(() => {
			fireRovoButtonUIEvent({
				source: productPage,
				action: 'hovered',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiSmartButton',
				attributes: {
					product,
					productPage,
					...analyticsAttributes,
					version: 'refresh',
				},
				product,
			});
		}, 1000);
	}, [fireRovoButtonUIEvent, product, productPage, analyticsAttributes, isComponentLoaded]);

	const onAISmartButtonLeave = React.useCallback(() => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}
	}, []);

	// technically we can also check the mode, as it should be in sync with the sidebar state (assuming that the consumer
	// is using toggleConversationAssistantUIMode in all entrypoints that trigger a mode switch).
	// The sidebarAPI is a bit more robust as it comes directly from the consumer, and thus will be used for the time being.
	if (sidebarApi.isSidebarOpen()) {
		return null;
	}

	if (mode === ConversationAssistantUIModes.MINI_MODAL) {
		return (
			<Box xcss={styles.wrapper}>
				<AIMiniModal
					product={product}
					cloudId={cloudId}
					orgId={orgId}
					isNav4Enabled={isNav4Enabled}
					sidebarApi={sidebarApi}
					getActions={getActions}
					topOffset={topOffset}
				/>
			</Box>
		);
	}

	return (
		<Box xcss={styles.wrapper}>
			<AISmartButtonUI
				ref={buttonRef}
				onClick={onAISmartButtonClick}
				isDisabled={isDisabled}
				onMouseEnter={onAISmartButtonEnter}
				onMouseLeave={onAISmartButtonLeave}
				sidebarApi={sidebarApi}
				isRefreshed={true}
			/>
		</Box>
	);
};
